// webapp/src/lib/offlineCache.js
//
// Phase 1 offline read cache. Wraps util.fetchApi to persist GET responses
// to IndexedDB and serve them when the network is unavailable. See
// docs/superpowers/specs/2026-05-06-offline-read-cache-design.md.

const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;       // 30 days
const DEFAULT_MAX_ENTRY_BYTES = 10 * 1024 * 1024;       // 10 MB

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit testing)

/**
 * Stable JSON.stringify — sorts object keys recursively so equivalent bodies
 * produce identical cache keys regardless of property order.
 */
function stableStringify(value) {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) {
        return '[' + value.map(stableStringify).join(',') + ']';
    }
    const keys = Object.keys(value).sort();
    return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}';
}

export function buildCacheKey(userId, customerId, endpoint, body) {
    const u = userId == null ? 'anon' : String(userId);
    const c = customerId == null ? 'anon' : String(customerId);
    return `${u}:${c}:${endpoint}?${stableStringify(body || {})}`;
}

/**
 * Returns true iff the response object matches the synthetic envelope that
 * preact-util's fetchApi returns when the underlying fetch threw. We use
 * this to avoid caching network failures and to detect "we should look
 * the cache up" on the way back out.
 */
export function isNetworkErrorEnvelope(response) {
    if (!response || typeof response !== 'object') return false;
    if (response.status !== 500) return false;
    return typeof response.message === 'string'
        && response.message.indexOf('ERROR fetching API:') === 0;
}

export function isFreshEntry(entry, ttlMs) {
    if (!entry || typeof entry.savedAt !== 'number') return false;
    return (Date.now() - entry.savedAt) < ttlMs;
}

// ---------------------------------------------------------------------------
// Wrapper factory (exported for unit testing — production code uses
// installOfflineCache instead, which builds and installs this for you).

export function makeWrappedFetchApi(originalFetchApi, storage, getUserId, getCustomerId, options = {}) {
    const ttlMs = options.ttlMs || DEFAULT_TTL_MS;
    const maxEntryBytes = options.maxEntryBytes || DEFAULT_MAX_ENTRY_BYTES;
    let firstCallLogged = false;

    return async function wrappedFetchApi(endpoint, opts = {}, body = {}) {
        const method = (opts && opts.method) ? String(opts.method).toUpperCase() : 'GET';
        const skipCache = opts && opts.skipCache === true;

        if (!firstCallLogged) {
            firstCallLogged = true;
            console.log('[offlineCache] first wrapped call', { endpoint, method });
        }

        // Pass-through cases — no caching, no interference.
        if (method !== 'GET' || skipCache) {
            return originalFetchApi(endpoint, opts, body);
        }

        // Compute key once, use it for both write and read fallback.
        let key;
        try {
            key = buildCacheKey(getUserId(), getCustomerId(), endpoint, body);
        } catch (e) {
            // If the resolvers blow up, behave like the bare original.
            return originalFetchApi(endpoint, opts, body);
        }

        // Live call. The token-refresh interceptor underneath us already
        // converts thrown fetch errors into the synthetic envelope, but we
        // catch defensively in case that ever changes.
        let response;
        try {
            response = await originalFetchApi(endpoint, opts, body);
        } catch (err) {
            response = {
                stats: {},
                status: 500,
                message: 'ERROR fetching API: ' + (err && err.message ? err.message : String(err)),
            };
        }

        if (isNetworkErrorEnvelope(response)) {
            // Network failed — try the cache.
            try {
                const entry = await storage.get(key);
                // Only replay plain 200s — legacy entries written before the
                // 200-only persist rule may hold stale 401/404 envelopes.
                if (isFreshEntry(entry, ttlMs) && entry.response && entry.response.status === 200) {
                    return Object.assign({}, entry.response, { _fromCache: true });
                }
            } catch (e) {
                // Storage unhappy — fall through to prefetch fallback.
            }

            // Exact-key miss. Feed endpoints: serve the newest cached page
            // with the same query identity (limit/offset may differ).
            if (getFeedConfig(endpoint)) {
                try {
                    const feedEntry = await findFeedFallback(
                        storage, endpoint, getUserId(), getCustomerId(), ttlMs, body,
                    );
                    if (feedEntry) {
                        const synthetic = buildFeedFallbackResponse(feedEntry, body);
                        if (synthetic) {
                            console.log(`[offlineCache] ${endpoint} feed fallback rows=${synthetic.data.length}`);
                            return synthetic;
                        }
                    }
                } catch (e) {
                    // Storage unhappy — fall through to original error envelope.
                }
            } else if (getSearchConfig(endpoint)) {
                try {
                    const prefetchEntry = await findPrefetchFallback(
                        storage, endpoint, getUserId(), getCustomerId(), ttlMs,
                    );
                    if (prefetchEntry) {
                        const sourceLen = prefetchEntry.response
                            && Array.isArray(prefetchEntry.response.data)
                            ? prefetchEntry.response.data.length : 0;
                        const synthetic = buildFallbackResponse(prefetchEntry, endpoint, body);
                        const filteredLen = synthetic && Array.isArray(synthetic.data) ? synthetic.data.length : 0;
                        console.log(`[offlineCache] ${endpoint} search="${(body && body.search) || ''}" cache=${sourceLen} matches=${filteredLen}`);
                        if (synthetic) return synthetic;
                    } else {
                        console.warn(`[offlineCache] ${endpoint} no prefetch data — run "Forbered for offline" while online`);
                    }
                } catch (e) {
                    // Storage unhappy — fall through to original error envelope.
                }
            }

            return response;
        }

        // Live success — fire-and-forget persist. Only plain 200s: replaying
        // a stale 401/404 envelope offline is never useful, and a cached 401
        // for /api/users/info would hang cold offline boot (stores/user.js
        // case 401 never sets isDoneLoadingUser).
        try {
            if (response && response.status === 200) {
                const serialized = JSON.stringify(response);
                if (serialized && serialized.length <= maxEntryBytes) {
                    const rows = response && Array.isArray(response.data) ? response.data.length : 0;
                    console.log(`[offlineCache] cached ${endpoint} rows=${rows} size=${(serialized.length / 1024).toFixed(0)} KB`);
                    storage.put(key, {
                        response,
                        savedAt: Date.now(),
                        userId: getUserId(),
                        customerId: getCustomerId(),
                        endpoint,
                        body,
                    }).catch(() => { /* ignore storage errors */ });
                } else if (serialized) {
                    console.warn(`[offlineCache] ${endpoint} too large to cache: ${(serialized.length / 1024 / 1024).toFixed(2)} MB`);
                }
            }
        } catch (e) {
            // ignore — caller still gets the live response
        }

        return response;
    };
}

// IndexedDB storage backend now lives in offlineStorage.js. We just bind a
// key/value view onto the apiResponses store.
import { createKeyValueStore } from './offlineStorage';
import { findPrefetchFallback, buildFallbackResponse, getSearchConfig } from './offlineSearchFallback';
import { getFeedConfig, findFeedFallback, buildFeedFallbackResponse } from './offlineFeedFallback';

export function createIdbStorage() {
    return createKeyValueStore('apiResponses');
}

// ---------------------------------------------------------------------------
// Public API

let _installed = false;
let _storage = null;

/**
 * Install the offline read cache by re-wrapping util.fetchApi.
 *
 * MUST be called AFTER installFetchApiInterceptor() so the cache layer sits
 * above the token-refresh layer (cache observes only "final" responses, not
 * 401-driven retries).
 *
 * Parameters:
 *   - util:           the preact-util default export (we re-bind its fetchApi)
 *   - getUserId:      () => number|null    — returns the current user id
 *   - getCustomerId:  () => number|null    — returns the current customer id
 *   - options:        { ttlMs?, maxEntryBytes? } — optional overrides
 *
 * Idempotent: subsequent calls are no-ops.
 */
export function installOfflineCache({ util, getUserId, getCustomerId, options } = {}) {
    if (_installed) {
        console.warn('[offlineCache] install skipped: already installed');
        return;
    }
    if (!util || typeof util.fetchApi !== 'function') {
        console.warn('[offlineCache] install skipped: util.fetchApi missing');
        return;
    }
    _installed = true;
    _storage = createIdbStorage();
    const original = util.fetchApi.bind(util);
    util.fetchApi = makeWrappedFetchApi(
        original,
        _storage,
        getUserId || (() => null),
        getCustomerId || (() => null),
        options || {},
    );
    console.log('[offlineCache] installed — util.fetchApi wrapped');
}

/**
 * Remove cache entries matching the given scope. Currently supports:
 *   { userId }  — drop all entries belonging to that user
 *
 * Called from logout / customer-switch flows in a follow-up edit.
 * No-op if the cache wasn't installed or storage is unavailable.
 */
export async function clearOfflineCache(scope = {}) {
    if (!_storage || !_storage.deleteWhere) return;
    try {
        if (scope.userId != null) {
            await _storage.deleteWhere(value => value && String(value.userId) === String(scope.userId));
        }
    } catch (e) {
        // ignore — cache cleanup is best-effort
    }
}

// Last checked 6.may 2026